import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Ticket as TicketIcon, Check, ChevronDown, Smartphone, CreditCard, Bitcoin } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Event, Ticket } from '../../types/event';
import { formatDate } from '../../utils/dateFormatter';
import { useTicketOrder } from '../../hooks/useTicketOrder';
import { createEventAttendee } from '../../services/event.service';
import { useToast } from '../../components/ui/use-toast';
import { convertFCFAToEUR } from '../../utils/currencyConverter';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../lib/supabase';
import { Input } from '../ui/input';
import { usePaymentStatus } from '../../hooks/usePaymentStatus';
import { useTickets } from '../../hooks/useTickets';
import { ChevronLeft } from 'lucide-react';
import { sendTicketEmail } from '../../services/emailService';
import { debugEventInfo } from './TicketPurchaseModalDebug';
import { useNavigate } from 'react-router-dom';

interface TicketPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  event: Event;
}

const TicketPurchaseModal: React.FC<TicketPurchaseModalProps> = ({
  isOpen,
  onClose,
  ticket: initialTicket,
  event
}) => {
  const { user } = useAuth();
  const { createOrder, updateOrderStatus } = useTicketOrder();
  const { showToast } = useToast();
  const [ticket, setTicket] = useState<Ticket>(initialTicket);
  const [showTicketSelector, setShowTicketSelector] = useState(false);
  const { tickets } = useTickets(event.id);
  const navigate = useNavigate();
  const [generatedQRCodes, setGeneratedQRCodes] = useState<string[]>([]);
  
  const [quantity, setQuantity] = useState(1);
  const [attendeeName, setAttendeeName] = useState(user?.user_metadata?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'payment' | 'processing' | 'confirmation'>('form');
  const isFreeTicket = ticket.price === 0 || ticket.ticket_type === 'free';
  const [hasAlreadyFreeTicket, setHasAlreadyFreeTicket] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>();
  const [currentOrderId, setCurrentOrderId] = useState<string>();
  
  // Mettre à jour le ticket si le ticket initial change
  useEffect(() => {
    setTicket(initialTicket);
    
    // Si c'est un ticket gratuit, forcer la quantité à 1
    if (initialTicket.price === 0 || initialTicket.ticket_type === 'free') {
      setQuantity(1);
    }
  }, [initialTicket]);
  
  // Vérifier si l'utilisateur a déjà un ticket gratuit pour cet événement
  useEffect(() => {
    const checkFreeTicketStatus = async () => {
      if (!isFreeTicket || !email) return;
      
      try {
        // Vérification par ID utilisateur si connecté
        if (user?.id) {
          const { data: existingOrdersByUserId } = await supabase
            .from('ticket_orders')
            .select('id')
            .eq('user_id', user.id)
            .eq('ticket_id', ticket.id)
            .eq('event_id', event.id);
            
          if (existingOrdersByUserId && existingOrdersByUserId.length > 0) {
            setHasAlreadyFreeTicket(true);
            return;
          }
        }
        
        // Vérification par email
        const { data: existingOrdersByEmail } = await supabase
          .from('ticket_orders')
          .select('id')
          .eq('email', email)
          .eq('ticket_id', ticket.id)
          .eq('payment_method', 'free');
          
        if (existingOrdersByEmail && existingOrdersByEmail.length > 0) {
          setHasAlreadyFreeTicket(true);
          return;
        }
        
        setHasAlreadyFreeTicket(false);
      } catch (error) {
        console.error('Erreur lors de la vérification du ticket gratuit:', error);
      }
    };
    
    checkFreeTicketStatus();
  }, [isFreeTicket, email, user?.id, ticket.id, event.id]);
  
  const { toast } = useToast();
  
  // Utiliser le hook de statut de paiement pour vérifier l'état du paiement
  const { status: paymentStatus, error: paymentError } = usePaymentStatus({
    reference: paymentReference,
    paymentMethod: paymentMethod || '',
    onSuccess: async () => {
      try {
        // Récupérer l'ID de la commande depuis le state ou localStorage
        let orderId = currentOrderId;
        
        // Si l'ID n'est pas dans le state (après redirection), essayer de le récupérer du localStorage
        if (!orderId && paymentReference) {
          orderId = localStorage.getItem(`order_${paymentReference}`);
        }
        
        if (!orderId) {
          // Essayer de récupérer via la référence de paiement (fallback)
          const { data: orderByRef } = await supabase
            .from('ticket_orders')
            .select('id')
            .eq('payment_reference', paymentReference)
            .maybeSingle();
            
          if (orderByRef) {
            orderId = orderByRef.id;
          }
        }

        if (!orderId) {
          throw new Error("Commande introuvable");
        }
        
        // Récupérer les détails de la commande avec l'ID
        const { data: orderData } = await supabase
          .from('ticket_orders')
          .select('*')
          .eq('id', orderId)
          .single();

        if (!orderData) {
          throw new Error("Détails de la commande introuvables");
        }

        // Vérifier si des tickets existent déjà pour cette commande
        const { data: existingTickets, error: ticketsError } = await supabase
          .from('event_attendees')
          .select('id')
          .eq('ticket_order_id', orderData.id);
          
        // Vérifier si le nombre de tickets existants correspond à la quantité commandée
        if (existingTickets && existingTickets.length === orderData.quantity) {
          console.log(`Tous les tickets (${existingTickets.length}/${orderData.quantity}) sont déjà créés pour cette commande. Pas de création de nouveaux tickets.`);
          // Mettre à jour le statut de la commande sans créer de nouveaux tickets
          await updateOrderStatus(orderData.id, 'completed', paymentReference || '');
        } else if (existingTickets && existingTickets.length > 0 && existingTickets.length < orderData.quantity) {
          // Certains tickets existent déjà, mais pas tous - créer les tickets manquants
          console.log(`${existingTickets.length}/${orderData.quantity} tickets existent déjà. Création des tickets manquants.`);
          
          // Mettre à jour le statut de la commande
          const updatedOrder = await updateOrderStatus(orderData.id, 'completed', paymentReference || '');
          
          // Créer uniquement les tickets manquants
          const ticketsToCreate = orderData.quantity - existingTickets.length;
          const qrCodes: string[] = [];
          
          for (let i = existingTickets.length; i < orderData.quantity; i++) {
            const qrCode = `${orderData.order_code}-${i + 1}-${uuidv4().substring(0, 6).toUpperCase()}`;
            qrCodes.push(qrCode);
            
            await createEventAttendee({
              event_id: event.id,
              user_id: user?.id,
              ticket_order_id: orderData.id,
              qr_code: qrCode,
              status: 'pending', // Statut initial 'pending' pour permettre la confirmation par l'admin
              attendee_name: attendeeName, // Utiliser directement le nom saisi dans le formulaire
              attendee_email: email,
              attendee_phone: phone
            });
          }
          
          // Envoyer l'email avec les billets
          // Vérifier que toutes les données nécessaires sont disponibles
          const eventName = event?.title || 'l\'événement';
          const eventDate = event?.start_date ? formatDate(event.start_date) : 'la date prévue';
          const eventLocation = event?.location || 'le lieu prévu';
          const ticketName = ticket?.name || 'Billet standard';
          const ticketPrice = ticket?.price || 0;
          
          try {
            await sendTicketEmail({
              to: email,
              name: attendeeName || 'Participant',
              subject: `Vos billets pour ${eventName}`,
              eventName: eventName,
              eventDate: eventDate,
              eventLocation: eventLocation,
              ticketType: ticketName,
              ticketPrice: ticketPrice,
              ticketQuantity: ticketsToCreate,
              orderCode: orderData.order_code,
              qrCodes: qrCodes,
              attendeePhone: phone
            });
            console.log('Email de billets envoyé avec succès');
          } catch (emailError) {
            console.error('Erreur lors de l\'envoi de l\'email de billets:', emailError);
          }
        } else {
          // Mettre à jour le statut de la commande
          const updatedOrder = await updateOrderStatus(orderData.id, 'completed', paymentReference || '');
          
          // Récupérer les informations de l'acheteur depuis la commande
          console.log('Nom du participant:', attendeeName);
          console.log('Données de commande:', orderData);
          
          // Créer les participants à l'événement (tickets) seulement après confirmation du paiement
          const qrCodes = [];
          for (let i = 0; i < orderData.quantity; i++) {
            const qrCode = uuidv4();
            qrCodes.push(qrCode);
            
            await createEventAttendee({
              event_id: event.id,
              ticket_order_id: orderData.id,
              attendee_name: attendeeName,
              attendee_email: email,
              attendee_phone: phone,
              qr_code: qrCode,
              status: 'pending',
              user_id: user?.id
            });
          }
          
          // Stocker les QR codes générés pour la redirection
          setGeneratedQRCodes(qrCodes);
          
          // Vérifier que toutes les données nécessaires sont disponibles
          const eventName = event?.title || 'l\'événement';
          const eventDate = event?.start_date ? formatDate(event.start_date) : 'la date prévue';
          const eventLocation = event?.location || 'le lieu prévu';
          const ticketName = ticket?.name || 'Billet standard';
          const ticketPrice = ticket?.price || 0;
          
          // Envoyer l'email avec les billets
          try {
            await sendTicketEmail({
              to: email,
              name: attendeeName || 'Participant',
              subject: `Vos billets pour ${eventName}`,
              eventName: eventName,
              eventDate: eventDate,
              eventLocation: eventLocation,
              ticketType: ticketName,
              ticketPrice: ticketPrice,
              ticketQuantity: orderData.quantity,
              orderCode: orderData.order_code,
              qrCodes: qrCodes,
              attendeePhone: phone
            });
            console.log('Email de billets envoyé avec succès');
          } catch (emailError) {
            console.error('Erreur lors de l\'envoi de l\'email de billets:', emailError);
          }
        }

        toast({
          title: "Paiement réussi !",
          description: "Votre commande a été confirmée. Vous recevrez vos tickets par email dans quelques instants.",
          variant: "default",
        });

        setStep('confirmation');
      } catch (error) {
        console.error('Erreur lors de la finalisation de la commande:', error);
        toast({
          title: "Erreur",
          description: "Le paiement a réussi mais une erreur est survenue lors de la finalisation.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Erreur de paiement",
        description: error.message,
        variant: "destructive",
      });
      setPaymentMethod(null);
      setProcessing(false);
    },
  });
  
  const MIN_INTERNATIONAL_PAYMENT_AMOUNT = 1500;
const MIN_CRYPTO_PAYMENT_AMOUNT = 1500;

const PAYMENT_METHODS = [
  { 
    id: 'mtn', 
    name: 'MTN & Orange Money (Cameroun)', 
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/avatars//om-momo.jpg',
    icon: <Smartphone className="h-5 w-5" />, 
    description: 'Paiement mobile au Cameroun'
  },
  {
    id: 'paypal', 
    name: 'PayPal',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/images.png',
    icon: <CreditCard className="h-5 w-5" />, 
    description: 'Paiement international',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
  {
    id: 'stripe', 
    name: 'Cartes Bancaires',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logos/card.jpg',
    icon: <CreditCard className="h-5 w-5" />, 
    description: 'Visa, Mastercard, etc.',
    minAmount: MIN_INTERNATIONAL_PAYMENT_AMOUNT
  },
  {
    id: 'crypto',
    name: 'Crypto Monnaies',
    logo: 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/avatars//online.ico',
    icon: <Bitcoin className="h-5 w-5" />, 
    description: 'Bitcoin, Ethereum, etc.',
    minAmount: MIN_CRYPTO_PAYMENT_AMOUNT
  }
];
  
  const totalAmount = ticket.price * quantity;
  
  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0 && value <= 100) {
      setQuantity(value);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!attendeeName || !email || !phone) {
      toast({
        title: "Champs manquants",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive",
      });
      return;
    }
    
    // Vérifier si c'est un ticket gratuit
    if (isFreeTicket) {
      try {
        setLoading(true);
        
        // Vérifier si l'utilisateur a déjà un ticket gratuit pour cet événement
        // Vérification par ID utilisateur si connecté
        if (user?.id) {
          const { data: existingOrdersByUserId, error: userIdError } = await supabase
            .from('ticket_orders')
            .select('id')
            .eq('user_id', user.id)
            .eq('ticket_id', ticket.id)
            .eq('event_id', event.id);
            
          if (userIdError) throw userIdError;
          
          if (existingOrdersByUserId && existingOrdersByUserId.length > 0) {
            toast({
              title: "Limite atteinte",
              description: "Vous avez déjà obtenu un ticket gratuit pour cet événement",
              variant: "destructive",
            });
            setLoading(false);
            return;
          }
        }
        
        // Vérification par email (même si l'utilisateur n'est pas connecté ou utilise un autre compte)
        const { data: existingOrdersByEmail, error: emailError } = await supabase
          .from('ticket_orders')
          .select('id')
          .eq('email', email)
          .eq('ticket_id', ticket.id)
          .eq('payment_method', 'free');
          
        if (emailError) throw emailError;
        
        if (existingOrdersByEmail && existingOrdersByEmail.length > 0) {
          toast({
            title: "Limite atteinte",
            description: "Cet email a déjà été utilisé pour obtenir un ticket gratuit pour cet événement",
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
        
        // Créer la commande pour le ticket gratuit
        const order = await createOrder({
          ticket_id: ticket.id,
          user_id: user?.id,
          quantity: 1, // Limiter à 1 ticket gratuit par utilisateur
          amount: 0,
          currency: ticket.currency,
          status: 'completed', // Marquer comme complété directement
          payment_method: 'free',
          email,
          phone,
          attendee_name: attendeeName,
          order_code: `FREE-${uuidv4().substring(0, 8).toUpperCase()}`
        });
        
        // Générer le QR code pour le ticket gratuit
        const qrCode = `${order.order_code}-1-${uuidv4().substring(0, 6).toUpperCase()}`;
        setGeneratedQRCodes([qrCode]);
        
        // Créer le ticket d'entrée
        await createEventAttendee({
          event_id: event.id,
          user_id: user?.id,
          ticket_order_id: order.id,
          qr_code: qrCode,
          status: 'pending',
          attendee_name: attendeeName,
          attendee_email: email,
          attendee_phone: phone
        });
        
        // Envoyer l'email avec le ticket
        await sendTicketEmail({
          to: email,
          name: attendeeName,
          subject: `Votre ticket gratuit pour ${event.title}`,
          eventName: event.title,
          ticketType: ticket.name,
          ticketPrice: 0,
          ticketQuantity: 1,
          orderCode: order.order_code || '',
          qrCodes: [qrCode],
          eventDate: formatDate(new Date(event.start_date).toISOString()),
          eventLocation: event.location || 'Lieu à confirmer'
        });
        
        toast({
          title: "Ticket obtenu !",
          description: "Votre ticket gratuit a été généré avec succès. Vous le recevrez par email.",
          variant: "default",
        });
        
        // Passer directement à l'étape de confirmation
        setStep('confirmation');
      } catch (error) {
        console.error('Erreur lors de la génération du ticket gratuit:', error);
        toast({
          title: "Erreur",
          description: error instanceof Error ? error.message : 'Une erreur est survenue',
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Pour les tickets payants, passer à l'étape de paiement
      setStep('payment');
    }
  };
  
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
    setPhoneError(null);
  };

  const handlePaymentMethodSelect = (method: string) => {
    setPhoneError(null);
    setPaymentMethod(method);
    
    // Préremplir le numéro de téléphone avec 237 pour les méthodes de paiement camerounaises
    if (method === 'mtn') {
      setPhoneNumber(phone.startsWith('237') ? phone : '237' + phone.replace(/[^0-9]/g, ''));
    } else {
      setPhoneNumber(''); 
    }
  };
  
  // Fonction pour simuler un paiement réussi
  const simulateSuccessfulPayment = async (order: any) => {
    try {
      setProcessing(true);
      
      // Mettre à jour le statut de la commande à "completed"
      await updateOrderStatus(order.id, 'completed');
      
      // Créer les tickets d'entrée
      for (let i = 0; i < quantity; i++) {
        await createEventAttendee({
          event_id: event.id,
          user_id: user?.id,
          ticket_order_id: order.id,
          qr_code: `${order.order_code}-${i + 1}-${uuidv4().substring(0, 6).toUpperCase()}`,
          status: 'pending'
        });
      }
      
      toast({
        title: "Paiement simulé réussi !",
        description: "Votre achat de ticket a été enregistré avec succès.",
        variant: "default",
      });
      
      setTimeout(() => {
        setStep('confirmation');
      }, 2000);
    } catch (error) {
      console.error('Erreur lors de la simulation du paiement:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la simulation du paiement.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      if (!paymentMethod) return;
      
      // Vérifier le format du numéro pour MTN
      if (paymentMethod === 'mtn' && !/^237\d{9}$/.test(phoneNumber)) {
        setPhoneError('Le numéro doit avoir 9 chiffres après 237');
        return;
      }
      
      // Vérifier le montant minimum pour les paiements internationaux et crypto
      if (['paypal', 'stripe'].includes(paymentMethod) && totalAmount < MIN_INTERNATIONAL_PAYMENT_AMOUNT) {
        toast({
          title: "Erreur",
          description: "Le montant minimum pour les paiements internationaux est de 1,500 FCFA.",
          variant: "destructive",
        });
        return;
      }
      
      if (paymentMethod === 'crypto' && totalAmount < MIN_CRYPTO_PAYMENT_AMOUNT) {
        toast({
          title: "Erreur",
          description: "Le montant minimum pour les paiements crypto est de 1,500 FCFA.",
          variant: "destructive",
        });
        return;
      }
      
      setLoading(true);
      setProcessing(true);

      // Création de la commande ticket
      const order = await createOrder({
        ticket_id: ticket.id,
        user_id: user?.id,
        quantity,
        amount: totalAmount,
        currency: ticket.currency,
        status: 'pending',
        payment_method: paymentMethod,
        email,
        phone,
        attendee_name: attendeeName, // Nom du participant qui utilisera le ticket
        order_code: `TKT-${uuidv4().substring(0, 8).toUpperCase()}`
      });
      
      // Simuler un paiement réussi
      if (paymentMethod === 'simulate') {
        simulateSuccessfulPayment(order);
        return;
      }
      
      // Préparer les données pour l'API de paiement
      const paymentOptions = {
        paymentMethod: paymentMethod === 'crypto' ? 'plisio' : paymentMethod,
        phoneNumber: paymentMethod === 'mtn' ? phoneNumber : undefined
      };
      
      // Pour les paiements internationaux, s'assurer que le montant est correctement converti
      let orderToProcess = { ...order };
      if (['paypal', 'stripe', 'crypto', 'plisio'].includes(paymentMethod)) {
        // Convertir le montant en EUR pour les paiements internationaux
        let amountEUR = convertFCFAToEUR(totalAmount);
        
        // S'assurer que le montant est au moins 1 EUR pour les paiements internationaux
        // Stripe et PayPal exigent généralement un minimum de 0.50 à 1 EUR
        const MIN_EUR_AMOUNT = 1.0;
        if (amountEUR < MIN_EUR_AMOUNT) {
          amountEUR = MIN_EUR_AMOUNT;
        }
        
        console.log(`Montant converti pour paiement international: ${totalAmount} FCFA = ${amountEUR} EUR (ajusté au minimum si nécessaire)`);
        orderToProcess.amount = amountEUR;
        orderToProcess.currency = 'EUR';
      }
      
      // Initialisation du paiement via le service mutualisé
      const response = await (await import('../../services/eventTicketPaymentService')).initiateTicketPayment(orderToProcess, paymentOptions);
      
      if (response.transaction.payment_reference) {
        setPaymentReference(response.transaction.payment_reference);
        setCurrentOrderId(order.id); // Stocker l'ID de commande pour le récupérer plus tard
        
        // Rediriger vers l'URL de paiement si disponible
        if (response.transaction.payment_url) {
          // Sauvegarder l'ID de commande dans le localStorage pour le récupérer après redirection
          localStorage.setItem(`order_${response.transaction.payment_reference}`, order.id);
          window.location.href = response.transaction.payment_url;
          return;
        }
      }
      
      // Ne pas créer les tickets ici, ils seront créés uniquement après confirmation du paiement
      // Attendre que le statut passe à 'success' via le hook usePaymentStatus
      setStep('processing');
      toast({
        title: "Paiement en cours",
        description: "Veuillez valider l'opération sur votre téléphone si nécessaire.",
        variant: "info",
      });
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-lg z-[100] animate-fade-in" />
        <Dialog.Content 
          className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[450px] max-h-[85vh] 
            rounded-xl bg-gradient-to-b from-white/10 to-black/30 backdrop-blur-md text-white 
            shadow-[0_0_50px_rgba(255,255,255,0.1)] focus:outline-none z-[101] 
            border border-white/20 animate-scale-in
            overflow-y-auto overscroll-contain
            p-4 sm:p-6"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <style jsx global>{`
            .Dialog__Content::-webkit-scrollbar {
              display: none;
            }
          `}</style>
          
          {/* Effet de brillance */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent shimmer-effect" />
          
          {/* Header */}
          <div className="relative flex flex-col items-center justify-center border-b border-white/10 pb-4 sm:pb-6">
            <div className="mb-4 text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                {step === 'form' ? 'Acheter un ticket' : 
                 step === 'payment' ? 'Paiement' : 
                 step === 'processing' ? 'Traitement du paiement' : 
                 'Confirmation'}
              </h2>
              {step === 'form' && (
                <div className="relative inline-flex items-center gap-4 px-6 py-3 bg-white/5 rounded-xl border border-white/10">
                  <div className="relative">
                    <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur-sm opacity-70"></div>
                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="relative w-14 h-14 rounded-full object-cover border-2 border-yellow-400"
                      />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-bold bg-gradient-to-r from-yellow-200 to-yellow-400 text-transparent bg-clip-text">{event.title}</p>
                    <p className="text-sm text-white/70">{formatDate(event.start_date)}</p>
                  </div>
                </div>
              )}
            </div>
            <Dialog.Close className="absolute right-0 top-0 text-white/70 hover:text-yellow-400 transition-colors">
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>
          
          <div className="relative p-4 sm:p-6">
            {step === 'form' && showTicketSelector && (
              <>
                <div className="mb-6">
                  <button 
                    onClick={() => setShowTicketSelector(false)}
                    className="flex items-center text-white/70 hover:text-white mb-4 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    Retour au formulaire
                  </button>
                  
                  <h3 className="font-medium text-lg text-white mb-4">Choisir un ticket</h3>
                  
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                    {tickets?.filter(t => t.is_active).map((availableTicket) => {
  const isSoldOut = availableTicket.available_quantity !== undefined && availableTicket.available_quantity !== null && availableTicket.available_quantity <= 0;
  return (
    <div
      key={availableTicket.id}
      className={`p-4 rounded-lg border transition-all relative ${availableTicket.id === ticket.id
        ? 'bg-yellow-500/20 border-yellow-400'
        : 'bg-white/5 border-white/10 hover:bg-white/10'} ${isSoldOut ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => {
        if (!isSoldOut) {
          setTicket(availableTicket);
          setShowTicketSelector(false);
        }
      }}
      aria-disabled={isSoldOut}
      tabIndex={isSoldOut ? -1 : 0}
    >
      <div className="flex justify-between items-start">
        <h4 className="font-medium text-white">{availableTicket.name}</h4>
        <span className="text-yellow-400 font-medium">
          {availableTicket.price === 0 ? 'Gratuit' : `${availableTicket.price.toLocaleString()} FCFA`}
        </span>
      </div>
      <div className="flex items-center mt-2 gap-2">
        <span className={`px-2 py-1 rounded text-xs font-semibold ${
          availableTicket.ticket_type === 'standard' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
          availableTicket.ticket_type === 'vip' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
          availableTicket.ticket_type === 'free' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
          availableTicket.ticket_type === 'early_bird' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
          'bg-gray-500/20 text-gray-300 border border-gray-500/30'
        }`}>
          {availableTicket.ticket_type === 'standard' ? 'Standard' :
            availableTicket.ticket_type === 'vip' ? 'VIP' :
            availableTicket.ticket_type === 'free' ? 'Gratuit' :
            availableTicket.ticket_type === 'early_bird' ? 'Early Bird' : availableTicket.ticket_type}
        </span>
        {isSoldOut && (
          <span className="ml-2 text-xs font-bold text-red-400">ÉPUISÉ</span>
        )}
        {!isSoldOut && availableTicket.available_quantity !== undefined && availableTicket.available_quantity !== null && (
          <span className="ml-2 text-xs text-white/70">
            {availableTicket.available_quantity} {availableTicket.available_quantity > 1 ? 'restants' : 'restant'}
          </span>
        )}
        {availableTicket.description && (
          <span className="ml-2 text-xs text-white/60 truncate">
            {availableTicket.description.length > 40
              ? availableTicket.description.substring(0, 40) + '...'
              : availableTicket.description}
          </span>
        )}
      </div>
      {isSoldOut && (
        <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center">
          <span className="text-red-400 font-bold text-lg">ÉPUISÉ</span>
        </div>
      )}
    </div>
  );
})}
                  </div>
                </div>
              </>
            )}
            
            {step === 'form' && !showTicketSelector && (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Message d'avertissement pour ticket gratuit déjà obtenu */}
                {isFreeTicket && hasAlreadyFreeTicket && (
                  <div className="p-4 rounded-lg bg-red-500/20 border border-red-500/50 mb-4">
                    <p className="text-white font-medium">
                      Vous avez déjà obtenu un ticket gratuit pour cet événement.
                    </p>
                    <p className="text-white/80 text-sm mt-1">
                      Un seul ticket gratuit est autorisé par personne.
                    </p>
                  </div>
                )}
                
                {/* Sélection de ticket */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-sm font-medium text-white">Ticket sélectionné</label>
                    <button
                      type="button"
                      onClick={() => setShowTicketSelector(true)}
                      className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                    >
                      Changer de ticket
                    </button>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <TicketIcon className="w-5 h-5 text-yellow-400" />
                      <span className="text-yellow-400 font-medium">
                        {ticket.price === 0 ? 'Gratuit' : `${ticket.price.toLocaleString()} FCFA`}
                      </span>
                      <span className="ml-3 px-2 py-1 rounded text-xs font-semibold ${
                        ticket.ticket_type === 'standard' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                        ticket.ticket_type === 'vip' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                        ticket.ticket_type === 'free' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                        ticket.ticket_type === 'early_bird' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                        'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                      }">
                        {ticket.ticket_type === 'standard' ? 'Standard' : 
                         ticket.ticket_type === 'vip' ? 'VIP' : 
                         ticket.ticket_type === 'free' ? 'Gratuit' : 
                         ticket.ticket_type === 'early_bird' ? 'Early Bird' : ticket.ticket_type}
                      </span>
                    </div>
                    {event.location && (
                      <span className="text-sm text-white/60">
                        📍 {event.location}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Quantité
                    </label>
                    <div className="relative">
                      {isFreeTicket ? (
                        <div className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white opacity-70">
                          1 ticket (limite pour ticket gratuit)
                        </div>
                      ) : (
                        <>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={quantity}
                            onChange={handleQuantityChange}
                            className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Nom du participant <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={attendeeName}
                        onChange={(e) => setAttendeeName(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                        placeholder="Nom complet"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Email <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                        placeholder="votre@email.com"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Téléphone <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-transparent"
                        placeholder="+237 6XX XXX XXX"
                      />
                    </div>
                  </div>
                  
                  <div className="border-t border-white/10 pt-6">
                    <div className="flex justify-between items-center mb-6">
                      <span className="font-medium text-white/80">Total</span>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-yellow-400">
                          {totalAmount === 0 ? 'Gratuit' : `${totalAmount.toLocaleString()} FCFA`}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isFreeTicket && hasAlreadyFreeTicket}
                      className="relative w-full flex items-center justify-center px-6 py-4 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold shadow-lg shadow-yellow-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent shimmer-effect" />
                      <span className="relative flex items-center">
                        {loading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black/20 mr-2"></div>
                            Traitement...
                          </>
                        ) : (
                          isFreeTicket ? 'Obtenir mon ticket gratuit' : 'Continuer vers le paiement'
                        )}
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            )}
            
            {step === 'payment' && (
              <>
                <div className="mb-6">
                  <div className="bg-white/5 p-4 rounded-lg border border-white/10 mb-6">
                    <h3 className="font-medium text-white mb-2">Récapitulatif de commande</h3>
                    <div className="space-y-2 text-sm text-white/80">
                      <div className="flex justify-between">
                        <span>Événement :</span>
                        <span className="text-right font-medium">{event.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Type de ticket :</span>
                        <span className="font-medium">
                          {ticket.ticket_type === 'standard' ? 'Standard' : 
                           ticket.ticket_type === 'vip' ? 'VIP' : 
                           ticket.ticket_type === 'free' ? 'Gratuit' : 
                           ticket.ticket_type === 'early_bird' ? 'Early Bird' : ticket.ticket_type}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Ticket :</span>
                        <span className="font-medium">{ticket.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Quantité :</span>
                        <span className="font-medium">{quantity} x {ticket.price.toLocaleString()} FCFA</span>
                      </div>
                      <div className="h-px bg-white/10 my-2"></div>
                      <div className="flex justify-between text-base font-bold">
                        <span>Total :</span>
                        <span className="text-yellow-400">{totalAmount.toLocaleString()} FCFA</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <p className="font-medium text-white/90 mb-3">Choisissez votre méthode de paiement</p>
                    
                    <div className="space-y-3">
                      {PAYMENT_METHODS.map((method) => (
                        <div key={method.id} className="mb-3">
                          <button
                            type="button"
                            onClick={() => handlePaymentMethodSelect(method.id)}
                            disabled={method.minAmount && totalAmount < method.minAmount}
                            className={`relative w-full flex items-center p-3 rounded-lg border transition-all overflow-hidden
                              ${paymentMethod === method.id
                                ? 'border-yellow-400 bg-yellow-400/10 text-yellow-400'
                                : method.minAmount && totalAmount < method.minAmount
                                  ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
                                  : 'border-white/10 bg-white/5 hover:bg-white/10'
                              }`}
                          >
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent shimmer-effect" />
                            <div className="relative flex items-center w-full">
                              <img
                                src={method.logo}
                                alt={method.name}
                                className="w-12 h-12 mr-3 object-contain"
                              />
                              <div>
                                <span className="font-medium text-sm sm:text-base">{method.name}</span>
                                <p className="text-xs text-gray-400">{method.description}</p>
                                {method.minAmount && totalAmount < method.minAmount && (
                                  <p className="text-xs text-yellow-500 mt-1">Minimum {method.minAmount.toLocaleString()} FCFA</p>
                                )}
                              </div>
                            </div>
                          </button>
                          
                          {/* Formulaire de paiement intégré directement après le moyen sélectionné */}
                          {paymentMethod === method.id && (
                            <div className="space-y-4 pl-2 pr-2 mt-3">
                              {/* Champ de téléphone pour MTN */}
                              {paymentMethod === 'mtn' && (
                                <div>
                                  <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-1">
                                    Numéro de téléphone
                                  </label>
                                  <Input
                                    id="phone"
                                    type="text"
                                    value={phoneNumber}
                                    onChange={handlePhoneNumberChange}
                                    placeholder="237xxxxxxxxx"
                                    className="bg-white/5 border-white/20 text-white placeholder-white/40"
                                  />
                                  {phoneError && (
                                    <p className="text-red-400 text-xs mt-1">{phoneError}</p>
                                  )}
                                </div>
                              )}
                              
                              {/* Bouton de paiement intégré directement après chaque méthode */}
                              <button
                                onClick={handlePayment}
                                disabled={loading || processing || 
                                  (method.id === 'mtn' && !phoneNumber) ||
                                  (method.id === 'paypal' && totalAmount < MIN_INTERNATIONAL_PAYMENT_AMOUNT) ||
                                  (method.id === 'stripe' && totalAmount < MIN_INTERNATIONAL_PAYMENT_AMOUNT) ||
                                  (method.id === 'crypto' && totalAmount < MIN_CRYPTO_PAYMENT_AMOUNT)
                                }
                                className="relative w-full flex items-center justify-center px-6 py-4 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-black font-semibold shadow-lg shadow-yellow-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent shimmer-effect" />
                                <span className="relative flex items-center">
                                  {loading || processing ? (
                                    <>
                                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black/20 mr-2"></div>
                                      Traitement en cours...
                                    </>
                                  ) : (
                                    <>
                                      Payer {['paypal', 'stripe', 'crypto'].includes(method.id) ? 
                                        <>€{convertFCFAToEUR(totalAmount).toFixed(2)}</> : 
                                        <>{totalAmount.toLocaleString()} FCFA</>}
                                    </>
                                  )}
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-4 border-t border-white/10 pt-6">
  {/* Le bouton de paiement global a été déplacé après chaque méthode de paiement */}
  {/* Fonctionnalité de simulation conservée dans le code mais bouton supprimé */}

                    
                    <button
                      onClick={() => setStep('form')}
                      className="w-full mt-4 text-white/80 py-3 px-4 rounded-lg border border-white/10 hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors"
                      disabled={loading}
                    >
                      Retour
                    </button>
                  </div>
                </div>
              </>
            )}
            
            {step === 'processing' && (
              <div className="text-center space-y-6">
                <div className="bg-white/5 p-6 rounded-lg border border-white/10 inline-flex flex-col items-center">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-400"></div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Paiement en cours...</h3>
                  <p className="text-white/70 mb-4">
                    Veuillez valider l'opération sur votre téléphone si nécessaire. Cette page se mettra à jour automatiquement.
                  </p>
                  <p className="text-sm text-white/50">
                    Référence: {paymentReference}
                  </p>
                </div>
              </div>
            )}
            
            {step === 'confirmation' && (
              <div className="text-center space-y-6">
                <div className="bg-white/5 p-6 rounded-lg border border-white/10 inline-flex flex-col items-center">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{isFreeTicket ? "Ticket obtenu !" : "Paiement réussi !"}</h3>
                  <p className="text-white/70 mb-4">
                    {isFreeTicket 
                      ? "Votre ticket gratuit a été généré avec succès. Vous le recevrez par email dans quelques instants."
                      : "Votre commande a été confirmée. Vous recevrez vos tickets par email dans quelques instants."}
                  </p>
                </div>
                
                {!isFreeTicket && (
                  <button
                    onClick={() => {
                      onClose();
                      // Redirection vers la page du ticket si un seul ticket, sinon vers "mes tickets"
                      if (generatedQRCodes.length === 1) {
                        navigate(`/tickets/${generatedQRCodes[0]}`);
                      } else {
                        navigate('/my-tickets');
                      }
                    }}
                    className="w-full px-6 py-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold transition-colors"
                  >
                    {generatedQRCodes.length === 1 ? 'Voir mon ticket' : 'Voir mes tickets'}
                  </button>
                )}
                
                {isFreeTicket && (
                  <button
                    onClick={() => onClose()}
                    className="w-full px-6 py-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-bold transition-colors"
                  >
                    Fermer
                  </button>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default TicketPurchaseModal;
